import React, { useEffect, useState } from "react";
import { useAuditLogs } from "../hooks/useAuditLogs";
import { AlertCircle, FileText, Search, ShieldOff } from "lucide-react";

function ActionBadge({ action }) {
  const styles = {
    create: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
    update: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30",
    delete: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
    activate: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/30",
    deactivate: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
    respond: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/30",
    pay: "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border capitalize ${styles[action] || "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"}`}>
      {action}
    </span>
  );
}

export default function AdminMarketplaceAuditLogs() {
  const { auditLogs, loading, error, fetchAuditLogs } = useAuditLogs();
  const [entityFilter, setEntityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const filtered = auditLogs.filter((log) => {
    const matchEntity = !entityFilter || log.entity_type === entityFilter;
    const matchAction = !actionFilter || log.action === actionFilter;
    const matchSearch = !search || log.entity_id?.includes(search) || log.performed_by_id?.includes(search);
    return matchEntity && matchAction && matchSearch;
  });

  const entityTypes = [...new Set(auditLogs.map((l) => l.entity_type).filter(Boolean))];
  const actions = [...new Set(auditLogs.map((l) => l.action).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin dark:border-indigo-500/20 dark:border-t-indigo-400" />
          <p className="text-sm text-slate-500 dark:text-slate-500">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center dark:bg-rose-500/10 dark:border-rose-500/30">
        <AlertCircle size={32} className="mx-auto text-rose-500 mb-2 dark:text-rose-400" />
        <p className="text-rose-700 font-medium dark:text-rose-400">Failed to load audit logs</p>
        <p className="text-sm text-rose-500 mt-1 dark:text-rose-400">You may not have admin permissions.</p>
        <button onClick={() => fetchAuditLogs()} className="mt-3 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Audit Logs</h1>
        <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-500">Track all marketplace actions and changes.</p>
      </div>

      {/* Access restriction notice */}
      <div className="flex items-start gap-4 p-5 bg-amber-50 border border-amber-200 rounded-xl dark:bg-amber-500/10 dark:border-amber-500/30">
        <ShieldOff size={24} className="text-amber-500 shrink-0 mt-0.5 dark:text-amber-400" />
        <div>
          <p className="font-semibold text-amber-800 dark:text-amber-400">Admin Access Required</p>
          <p className="text-sm text-amber-700 mt-0.5 dark:text-amber-400">
            Audit logs are only accessible to Marketplace Administrators. Store accounts do not have permission to view this section.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm opacity-50 pointer-events-none dark:bg-slate-900 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
            />
          </div>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
          >
            <option value="">All Entities</option>
            {entityTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:focus:ring-indigo-500/30"
          >
            <option value="">All Actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden dark:bg-slate-900 dark:border-slate-800">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto text-slate-300 mb-3 dark:text-slate-700" />
            <p className="text-slate-500 text-sm dark:text-slate-500">No audit logs available for your account.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-800/60 dark:border-slate-800">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Entity</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Performed By</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Role</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Date</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{log.entity_type}</p>
                      <p className="text-xs text-slate-400 font-mono dark:text-slate-500">{log.entity_id?.slice(0, 8)}...</p>
                    </td>
                    <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                    <td className="px-4 py-3 text-slate-600 text-xs font-mono dark:text-slate-400">{log.performed_by_id?.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-slate-500 capitalize dark:text-slate-500">{log.performed_by_role?.toLowerCase()}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs dark:text-slate-500">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      {log.before || log.after ? (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">View</summary>
                          <div className="mt-2 p-3 bg-slate-50 rounded-lg text-left space-y-1 dark:bg-slate-800/60">
                            {log.before && (
                              <div>
                                <p className="font-medium text-slate-600 dark:text-slate-400">Before:</p>
                                <pre className="text-xs text-slate-500 whitespace-pre-wrap dark:text-slate-500">{JSON.stringify(log.before, null, 2)}</pre>
                              </div>
                            )}
                            {log.after && (
                              <div>
                                <p className="font-medium text-slate-600 dark:text-slate-400">After:</p>
                                <pre className="text-xs text-slate-500 whitespace-pre-wrap dark:text-slate-500">{JSON.stringify(log.after, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </details>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-600">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
